import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";

export interface CraveCategory {
  id: number;
  customerId: number;
  name: string;
  primaryMapping: number | null;
  secondaryMapping: number | null;
  createdAt: string;
  createdBy: string;
}

export interface CraveCategoryCreateRequest {
  name: string;
  customerId: number;
  primaryMapping?: number;
  secondaryMapping?: number;
}

export interface CraveCategoryUpdateRequest {
  id: number;
  name?: string;
  primaryMapping?: number | null;
  secondaryMapping?: number | null;
}

export interface CraveCategoryResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export class CraveCategoryService extends BaseApiService {
  private static instance: CraveCategoryService;

  private constructor() {
    super();
  }

  public static getInstance(): CraveCategoryService {
    if (!CraveCategoryService.instance) {
      CraveCategoryService.instance = new CraveCategoryService();
    }
    return CraveCategoryService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async create(
    createRequest: CraveCategoryCreateRequest,
  ): Promise<CraveCategoryResponse<CraveCategory>> {
    try {
      const response = await this.post<{ data: CraveCategory }>(
        "/craveCategory",
        createRequest,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create crave category",
      };
    }
  }

  public async getById(id: number): Promise<CraveCategoryResponse<CraveCategory>> {
    try {
      const response = await this.get<{ data: CraveCategory }>(
        `/craveCategory/${id}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch crave category",
      };
    }
  }

  public async getByCustomerId(
    customerId: number,
  ): Promise<CraveCategoryResponse<CraveCategory[]>> {
    try {
      const response = await this.get<{ data: CraveCategory[] }>(
        `/craveCategory/customer/${customerId}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch crave categories",
      };
    }
  }

  public async update(
    updateRequest: CraveCategoryUpdateRequest,
  ): Promise<CraveCategoryResponse<void>> {
    try {
      await this.put<void>("/craveCategory", updateRequest, this.getCoreBaseUrl());
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update crave category",
      };
    }
  }

  public async deleteCraveCategory(id: number): Promise<CraveCategoryResponse<void>> {
    try {
      await this.delete<void>(`/craveCategory/${id}`, this.getCoreBaseUrl());
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to delete crave category",
      };
    }
  }
}
